import React, { memo } from 'react';
import { useLists } from '../../context';
import { useLang } from '../../i18n';
import { useListNames } from '../../hooks/useListNames';
import { InlineName, TargetSelect, EditTools } from './parts';

/**
 * One closing-sheet item. Walking: the box adds it to (or takes it off) the Today board.
 * Editing: the box picks it for bulk delete, and the name, list and position are editable.
 * Memoized — `actions` never changes, so a row re-renders only when its own item, tick or
 * selection does.
 *
 *   item, sub, section     the item and the tree above it
 *   subTarget              the list its sub-section feeds (item override aside)
 *   checked                on the board now · selected  picked for delete · editing
 *   index, siblings        its place among the sub-section's items (for ▲▼)
 *   actions                useClosingActions() · onSelect(id) — toggle the pick
 */
export const ClosingItemRow = memo(function ClosingItemRow({
  item, sub, section, subTarget, checked, selected, editing, index, siblings, actions, onSelect,
}) {
  const { t, ln } = useLang();
  const { catById, route } = useLists();
  const { short } = useListNames();
  const target = item.target || subTarget;
  const lands = route(item.name, target);   // the list a tap actually adds it to
  const overrides = item.target && item.target !== subTarget;
  const rowCls = editing ? (selected ? ' do-cl__item--sel' : '') : (checked ? ' do-cl__item--on' : '');
  const toggle = () => actions.toggle(item, sub, section);
  const select = () => onSelect(item.id);
  return (
    <div className={`do-cl__item${rowCls}`}>
      {editing ? (
        <button className="do-cl__check" onClick={select} aria-pressed={selected} aria-label={t('closing.selectItem', { name: ln(item.name) })}>
          <span className="do-cl__box">{selected ? '✓' : ''}</span>
        </button>
      ) : (
        <button className="do-cl__check" onClick={toggle} aria-pressed={checked}
          title={checked ? t('closing.tapRemove', { cat: short(lands) }) : t('closing.tapAdd', { cat: short(lands) })}>
          <span className="do-cl__box">{checked ? '✓' : ''}</span>
        </button>
      )}
      {editing
        ? <InlineName className="do-cl__item-name-edit" value={item.name} ariaLabel={t('closing.itemName')} onSave={v => actions.rename(item, v)} />
        : <button className="do-cl__item-name" onClick={toggle}>{ln(item.name)}</button>}
      {item.note && <span className="do-cl__note">{item.note}</span>}
      {(overrides || lands !== target) && !editing && (
        <span className="do-cl__chip" style={{ '--sc': catById(lands).color }}>{catById(lands).icon} {short(lands)}</span>
      )}
      {editing && (
        <div className="do-cl__item-tools">
          {item.keepList && <span className="do-cl__pin" title={t('sort.keptHint')} aria-label={t('sort.keptHint')}>📌</span>}
          <TargetSelect value={target} ariaLabel={t('closing.itemTarget')} onChange={v => actions.setItemTarget(item, subTarget, v)} />
          <EditTools onUp={() => actions.moveItem(siblings, index, -1)} onDown={() => actions.moveItem(siblings, index, 1)}
            canUp={index > 0} canDown={index < siblings.length - 1} onDelete={select} deleteLabel={t('closing.deleteItem')} />
        </div>
      )}
    </div>
  );
});
